import { Navigate, Routes, Route, useParams, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import PrivateRoute from '../components/common/PrivateRoute';
import themeCardapio from '../components/Products/Cardapio/themeCardapio';
import { getApiBaseUrl } from '../utils/apiBaseUrl';

import Dashboard from '../components/Products/Cardapio/pages/Dashboard.jsx';
import TagsPage from '../components/Products/Cardapio/pages/TagsPage.jsx';
import PublicMenu from '../components/Products/Cardapio/pages/PublicMenu.jsx';

const API_URL = getApiBaseUrl();

const keepCurrentOrigin = (targetUrl) => {
  if (!targetUrl || typeof targetUrl !== 'string') return targetUrl;
  if (!/^https?:\/\//i.test(targetUrl)) return targetUrl;
  try {
    const url = new URL(targetUrl);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return targetUrl;
  }
};

export const CheckinCardapioRedirect = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const mesa = searchParams.get('mesa');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const retryKey = `checkin-cardapio-redirect:retry:${slug || ''}:${mesa || ''}`;
    const clearRetry = () => {
      try {
        window.sessionStorage.removeItem(retryKey);
      } catch {
        // ignore
      }
    };
    const run = async () => {
      try {
        const qs = mesa ? `?mesa=${encodeURIComponent(mesa)}` : '';
        const res = await fetch(`${API_URL}/checkin/cardapio/${slug}${qs}`);
        if (res.redirected) {
          clearRetry();
          window.location.href = keepCurrentOrigin(res.url);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (data?.data?.redirectUrl) {
          clearRetry();
          window.location.href = keepCurrentOrigin(data.data.redirectUrl);
          return;
        }
        if (data?.redirectUrl) {
          clearRetry();
          window.location.href = keepCurrentOrigin(data.redirectUrl);
          return;
        }
        if (data?.token && data?.comanda_id) {
          clearRetry();
          const url = `/cardapio/${slug}?mesa=${encodeURIComponent(mesa || '')}&token=${encodeURIComponent(data.token)}`;
          window.location.href = url;
          return;
        }
        if (res.status === 404) {
          throw new Error('Check-in não encontrado');
        }
        throw new Error(data?.message || 'Falha ao gerar check-in');
      } catch (err) {
        console.error('Erro ao redirecionar check-in:', err);
        const message = err?.message || 'Falha ao gerar check-in';
        const shouldRetry = message === 'Falha ao gerar check-in';
        if (shouldRetry) {
          let retryCount = 0;
          try {
            retryCount = Number(window.sessionStorage.getItem(retryKey) || 0) || 0;
          } catch {
            retryCount = 0;
          }
          if (retryCount < 2) {
            try {
              window.sessionStorage.setItem(retryKey, String(retryCount + 1));
            } catch {
              // ignore
            }
            window.location.reload();
            return;
          }
          clearRetry();
          setError('Falha ao gerar check-in');
          return;
        }
        clearRetry();
        setError(message || 'Erro ao redirecionar');
      }
    };
    if (slug) run();
  }, [slug, mesa]);

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
      <CircularProgress />
      <Typography ml={2}>Gerando acesso ao cardápio...</Typography>
    </Box>
  );
};

const CardapioRoutes = () => (
  <ThemeProvider theme={themeCardapio}>
    <CssBaseline />
    <Routes>
      <Route
        path="/"
        element={
          <PrivateRoute product="cardapio">
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route path="checkin/cardapio/:slug" element={<CheckinCardapioRedirect />} />
      <Route path=":slug" element={<PublicMenu />} />
      <Route
        path="tags"
        element={
          <PrivateRoute product="cardapio">
            <TagsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="ifood"
        element={<Navigate to="/cardapio" replace />}
      />
    </Routes>
  </ThemeProvider>
);

export default CardapioRoutes;
